import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material';
import Box from '@/components/ui/Box/Box';
import Button from '@/components/ui/Button/Button';
import Typography from '@/components/ui/Typography/Typography';
import Loading from '@/components/business/Loading/Loading';
import Page from '@/components/business/Page/Page';
import { useCompany } from '@/providers/CompanyProvider/CompanyProvider';
import { useRepairers } from '@/providers/RepairersProvider/RepairersProvider';
import type { Company, CompanyAddress } from '@/types/types';
import CompanyInfoForm from './components/CompanyInfoForm/CompanyInfoForm';
import RepairerCard from './components/RepairerCard/RepairerCard';
import RepairerDetails from './components/RepairerDetails/RepairerDetails';
import type { CompanyInfoFormValues } from './components/CompanyInfoForm/ICompanyInfoForm';
import type { AdminTab } from './IAdminPage';
import { styles } from './styles';

const buildAddressPayload = (
    prefix: 'mailing' | 'billing',
    form: CompanyInfoFormValues,
): CompanyAddress => {
    const get = (key: string) =>
        (form as unknown as Record<string, string | undefined>)[`${prefix}_${key}`] ?? '';
    return {
        formatted_address: get('formatted_address') || null,
        street_number: get('street_number') || null,
        street_name: get('street_name') || null,
        subpremise: get('subpremise') || null,
        city: get('city') || null,
        state: get('state') || null,
        zip: get('zip') || null,
        country: get('country') || null,
        place_id: (form as unknown as Record<string, string | undefined>)[
            `${prefix}_place_id`
        ],
    };
};

const toCompanyPayload = (form: CompanyInfoFormValues): Partial<Company> => ({
    name: form.name,
    mailing_address: buildAddressPayload('mailing', form),
    billing_address: buildAddressPayload('billing', form),
    contact_info: {
        name: null,
        email: form.contact_email || null,
        phone_number: form.contact_phone || null,
    },
});

const AdminPage = () => {
    const theme = useTheme();
    const companyCtx = useCompany();
    const repairersCtx = useRepairers();

    const isRepairerSide =
        companyCtx.state.company?.marketplace_side === 'Repairer';
    const connectionLabel = isRepairerSide ? 'Supplier' : 'Repairer';
    const connectionsLabelPlural = isRepairerSide ? 'Connections' : 'Repairers';

    const tabs: { id: AdminTab; label: string }[] = useMemo(
        () => [
            { id: 'company', label: 'Company Info' },
            { id: 'connections', label: connectionsLabelPlural },
        ],
        [connectionsLabelPlural],
    );

    const [activeTab, setActiveTab] = useState<AdminTab>('company');
    const [activeRepairerId, setActiveRepairerId] = useState<string | null>(null);

    useEffect(() => {
        void companyCtx.actions.refreshCompany();
    }, []);

    useEffect(() => {
        if (activeTab === 'connections') {
            void repairersCtx.actions.refreshRepairers();
        }
    }, [activeTab]);

    useEffect(() => {
        if (activeTab !== 'connections') return;
        const first = repairersCtx.state.repairers[0];
        setActiveRepairerId((prev) => prev ?? first?.id ?? null);
    }, [activeTab, repairersCtx.state.repairers]);

    const activeRepairer = repairersCtx.state.repairers.find(
        (r) => r.id === activeRepairerId,
    );

    const handleSaveCompany = async (values: CompanyInfoFormValues) => {
        await companyCtx.actions.updateCompany(toCompanyPayload(values));
    };

    const companyLoaded = Object.values(companyCtx.state.company).length > 0;

    return (
        <Page title="Administration">
            <Box sx={styles.pillRow}>
                {tabs.map((tab, idx) => (
                    <Button
                        key={tab.id}
                        sx={styles.pillBtn(activeTab === tab.id, theme, idx)}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </Button>
                ))}
            </Box>
            <Box sx={styles.contentContainer(theme)}>
                {activeTab === 'company' && (
                    <Box sx={styles.companyScroll}>
                        {companyLoaded ? (
                            <CompanyInfoForm
                                key={companyCtx.state.company.id ?? 'company'}
                                company={companyCtx.state.company}
                                onSave={handleSaveCompany}
                            />
                        ) : (
                            <Loading
                                loading
                                fullScreen={false}
                                width={150}
                                height={750}
                                timeoutMessage="Could not load company."
                            />
                        )}
                    </Box>
                )}
                {activeTab === 'connections' && (
                    <Box sx={styles.layoutRow}>
                        <Box sx={styles.listPanel(theme, Boolean(activeRepairer))}>
                            {repairersCtx.state.loading &&
                            repairersCtx.state.repairers.length === 0 ? (
                                    <Loading
                                        loading
                                        fullScreen={false}
                                        width={120}
                                        height={120}
                                        timeoutMessage={`Could not load ${connectionsLabelPlural.toLowerCase()}.`}
                                    />
                                ) : repairersCtx.state.repairers.length === 0 ? (
                                    <Box sx={styles.emptyState}>
                                        <Typography>
                                        No {connectionsLabelPlural.toLowerCase()} yet.
                                        </Typography>
                                    </Box>
                                ) : (
                                    [...repairersCtx.state.repairers]
                                        .sort((a, b) =>
                                            (a.name ?? a.email ?? '').localeCompare(
                                                b.name ?? b.email ?? '',
                                                undefined,
                                                { numeric: true, sensitivity: 'base' },
                                            ),
                                        )
                                        .map((r) => (
                                            <RepairerCard
                                                key={r.id}
                                                repairer={r}
                                                isActive={r.id === activeRepairerId}
                                                onClick={setActiveRepairerId}
                                            />
                                        ))
                                )}
                        </Box>
                        <Box sx={styles.detailsPanel(!activeRepairer)}>
                            {activeRepairer ? (
                                <>
                                    <Box
                                        sx={styles.mobileBackBar(theme)}
                                        onClick={() => setActiveRepairerId(null)}
                                    >
                                        ← Back to {connectionsLabelPlural.toLowerCase()}
                                    </Box>
                                    <RepairerDetails
                                        key={activeRepairer.id}
                                        repairer={activeRepairer}
                                        label={connectionLabel}
                                    />
                                </>
                            ) : (
                                <Box sx={styles.emptyState}>
                                    <Typography>
                                        Select a {connectionLabel.toLowerCase()} to see details.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        </Page>
    );
};

export default AdminPage;
